import { ChoicesArticle, ChoicesComponentFilter } from "@generated"
import { useRouter } from "next/router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export function useConfiguratorNavigation(
  items: ChoicesArticle[],
  tab: ChoicesComponentFilter
) {
  const router = useRouter()
  const isInitialised = useRef(false)
  const [state, setState] = useState({
    stone: null,
    frame: null,
    base: null,
  })

  // Run this effect if router.query updates, but only once
  useEffect(() => {
    if (isInitialised.current) {
      return
    }
    setState({
      stone: (router.query.stone as string) ?? null,
      frame: (router.query.frame as string) ?? null,
      base: (router.query.base as string) ?? null,
    })
    isInitialised.current = true
  }, [router.query])

  // Sync state with the URL if it changes
  useEffect(() => {
    const origin = window.location.href
    const url = new URL(origin)
    if (state.stone) {
      url.searchParams.set("stone", state.stone)
    }
    if (state.base) {
      url.searchParams.set("base", state.base)
    }
    if (state.frame) {
      url.searchParams.set("frame", state.frame)
    }
    if (origin !== url.href) {
      router.replace(url.href, undefined, { shallow: true })
    }
  }, [state, tab, router])

  // Lookup the active id, find the index and the item
  const activeId = state[tab.toLowerCase()]
  const activeIndex = useMemo(() => {
    const newIndex = items.findIndex((item) => item.id === activeId)
    const newActiveItem = newIndex >= 0 ? items.at(newIndex) : items.at(0)

    // If the articleCombination is a hippiechic earring it could be that the active item is not in the list of items anymore
    // That can happen when a user selects a different base with a different material than the previous one
    // here we
    if (newActiveItem && newActiveItem.id !== activeId) {
      setState((state) => ({ ...state, [tab.toLowerCase()]: newActiveItem.id }))
    }

    return newIndex >= 0 ? newIndex : 0
  }, [activeId, tab, items])

  const activeItem = activeIndex >= 0 ? items.at(activeIndex) : items.at(0)

  // Navigation handlers
  const prev = useCallback(() => {
    const prevItem = items[activeIndex - 1]
    if (prevItem) {
      setState((state) => ({ ...state, [tab.toLowerCase()]: prevItem.id }))
    }
  }, [activeIndex, items, tab])

  const next = useCallback(() => {
    const nextItem = items.at(activeIndex + 1)
    if (nextItem) {
      setState((state) => ({ ...state, [tab.toLowerCase()]: nextItem.id }))
    }
  }, [activeIndex, items, tab])

  const goTo = useCallback(
    (index: number) => {
      const item = items.at(index)
      if (item) {
        setState((state) => ({ ...state, [tab.toLowerCase()]: item.id }))
      }
    },
    [items, tab]
  )

  const canMovePrev = typeof items[activeIndex - 1] !== "undefined"
  const canMoveNext = typeof items[activeIndex + 1] !== "undefined"

  useEffect(() => {
    function handleEvent(event: KeyboardEvent) {
      if (event.key === "ArrowLeft" && canMovePrev) {
        prev()
      }
      if (event.key === "ArrowRight" && canMoveNext) {
        next()
      }
    }
    window.addEventListener("keyup", handleEvent)
    return () => window.removeEventListener("keyup", handleEvent)
  }, [canMovePrev, canMoveNext, prev, next])
  return {
    prev,
    next,
    goTo,
    activeItem,
    activeIndex,
    canMovePrev,
    canMoveNext,
    status: activeItem ? "ready" : "loading",
  }
}
